import React from "react";
import "./RelatorioCalibracaoPrensaPrint.css";
import LogoRelatorio, { useMarcaDaguaRelatorio } from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";

function formatarDataBR(dataISO) {
  if (!dataISO) return "-";

  if (typeof dataISO === "string" && dataISO.includes("-")) {
    const [ano, mes, dia] = dataISO.split("-");
    return `${dia}/${mes}/${ano}`;
  }

  return dataISO;
}

function numeroBR(valor, casas = 2) {
  if (valor === null || valor === undefined || valor === "" || valor === "-") {
    return "-";
  }

  const numero = Number(String(valor).replace(",", "."));
  if (Number.isNaN(numero)) return valor;

  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas
  });
}

function inteiroBR(valor) {
  if (valor === null || valor === undefined || valor === "" || valor === "-") {
    return "-";
  }

  const numero = Number(String(valor).replace(",", "."));
  if (Number.isNaN(numero)) return valor;

  return numero.toLocaleString("pt-BR", {
    maximumFractionDigits: 0
  });
}

function obterMediaKgf(resultado) {
  return resultado.mediaKgf ?? resultado.media ?? "-";
}

function obterMediaKn(resultado) {
  if (resultado.mediaKn !== undefined && resultado.mediaKn !== "") {
    return resultado.mediaKn;
  }

  const mediaInformada = String(obterMediaKgf(resultado)).trim();
  if (!mediaInformada || mediaInformada === "-") return "-";

  const mediaKgf = Number(mediaInformada.replace(",", "."));
  if (Number.isNaN(mediaKgf)) return "-";

  return mediaKgf * 0.00980665;
}

function obterNumeroCodigo(dados = {}) {
  const codigoInformado = String(dados.codigo || "").trim();
  const matchCodigo =
    codigoInformado.match(/RE-CAL\s*-\s*(\d+)/i) || codigoInformado.match(/(\d{3,})/);

  if (matchCodigo?.[1]) {
    return matchCodigo[1].padStart(4, "0");
  }

  if (matchCodigo?.[0]) {
    return matchCodigo[0].replace(/\D/g, "").padStart(4, "0");
  }

  const certificado = String(dados.certificado || "").trim();
  const matchCertificado = certificado.match(/(\d{2,})/g);

  if (matchCertificado?.length) {
    const maiorGrupo = matchCertificado.sort((a, b) => b.length - a.length)[0];
    return maiorGrupo.padStart(4, "0");
  }

  return "0000";
}

function formatarValidade(valor) {
  const texto = String(valor || "").trim();
  if (!texto) return "1 ano";
  if (/ano/i.test(texto)) return texto;
  return `${texto} ano`;
}

function formatarCapacidade(valor) {
  const texto = String(valor || "").trim();
  if (!texto) return "-";
  if (/tf$/i.test(texto)) return texto;
  return `${texto} tf`;
}

function formatarValorDivisao(valor) {
  const texto = String(valor || "").trim();
  if (!texto) return "-";
  if (/kn$/i.test(texto)) return texto;
  return `${texto} kN`;
}

function imprimirPreviewCalibracao() {
  const styleId = "print-style-calibracao-prensa";
  let style = document.getElementById(styleId);

  if (!style) {
    style = document.createElement("style");
    style.id = styleId;
    style.media = "print";
    style.textContent = "@page { size: A4 portrait; margin: 5mm; }";
    document.head.appendChild(style);
  }

  const limpar = () => {
    const styleAtual = document.getElementById(styleId);
    if (styleAtual) {
      styleAtual.remove();
    }
    window.removeEventListener("afterprint", limpar);
  };

  window.addEventListener("afterprint", limpar);
  window.print();
}

export default function RelatorioCalibracaoPrensaPrint({ dados = {}, onVoltar }) {
  const marcaDaguaUrl = useMarcaDaguaRelatorio();
  const resultados = Array.isArray(dados.resultados) ? dados.resultados : [];
  const anoAtual = new Date().getFullYear();
  const numeroCodigo = obterNumeroCodigo(dados);
  const codigo = `RE-CAL - ${numeroCodigo}/${anoAtual}`;
  useNomeArquivoPdf(onVoltar ? codigo : "");
  const certificadoCalibracao = numeroCodigo;

  const equipamento = dados.equipamento
    ? `MÁQUINA DE ENSAIO DE COMPRESSÃO ${String(dados.equipamento).toUpperCase()}`
    : "MÁQUINA DE ENSAIO DE COMPRESSÃO ELÉTRICA";

  const descricaoEquipamento = dados.classe
    ? `${equipamento} - CLASSE ${dados.classe}`
    : equipamento;

  const fornecedor = dados.fornecedor || "";
  const identificacaoPadrao = dados.identificacaoPadrao || "";
  const laboratorioPadrao = dados.laboratorioPadrao || "";
  const certificadoPadrao = dados.certificadoPadrao || "";
  const dataPadrao = formatarDataBR(dados.dataPadrao || "");
  const validadePadrao = dados.validadePadrao || "";

  return (
    <div className="relatorio-calibracao-print">
      {onVoltar && (
        <div className="acoes-print-calibracao">
          <button onClick={onVoltar} className="btn-voltar-calibracao">Voltar</button>
          <button onClick={imprimirPreviewCalibracao} className="btn-imprimir-calibracao">
            Imprimir
          </button>
        </div>
      )}

      <div
        className="pagina-calibracao"
        style={{
          "--marca-dagua-calibracao": `url("${marcaDaguaUrl}")`
        }}
      >
        <table className="tabela-calibracao bloco-cabecalho">
          <colgroup>
            <col style={{ width: "18%" }} />
            <col style={{ width: "67%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td className="celula-logo">
                <LogoRelatorio alt="Logo" className="logo-calibracao" variante="calibracao-prensa" />
              </td>

              <td className="titulo-principal">
                SISTEMA DE GESTÃO DA QUALIDADE
                <br />
                <span>Relatório de ensaio - Certificado de calibração de prensa</span>
              </td>

              <td className="celula-revisao">
                Revisão 01
                <br />
                20/11/2024
              </td>
            </tr>

            <tr>
              <td className="linha-data-codigo" colSpan="3">
                <div className="cabecalho-data-codigo">
                  <div className="linha-data">
                    <strong>DATA</strong>
                    <span>{formatarDataBR(dados.data || dados.dataCalibracao)}</span>
                  </div>

                  <div className="linha-codigo">
                    <strong>CÓDIGO:</strong> {codigo}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="tabela-calibracao bloco-resumo">
          <colgroup>
            <col style={{ width: "44%" }} />
            <col style={{ width: "33%" }} />
            <col style={{ width: "23%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td>
                <strong>Certificado de calibração nº:</strong> {certificadoCalibracao}
              </td>
              <td>
                <strong>Data da calibração:</strong> {formatarDataBR(dados.dataCalibracao)}
              </td>
              <td>
                <strong>Validade:</strong> {formatarValidade(dados.validade)}
              </td>
            </tr>
          </tbody>
        </table>

        <table className="tabela-calibracao bloco-cliente">
          <tbody>
            <tr>
              <td>
                <strong>EMPRESA/CONTRATANTE:</strong> {dados.empresa || "-"}
              </td>
            </tr>
            <tr>
              <td>
                <strong>EMPREENDIMENTO:</strong> {dados.obra || "-"}
              </td>
            </tr>
            <tr>
              <td>
                <strong>ENDEREÇO:</strong> {dados.endereco || "-"}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="secao-calibracao">1. DESCRIÇÃO DO EQUIPAMENTO CALIBRADO.</div>

        <table className="tabela-calibracao bloco-detalhes">
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "28%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td className="rotulo">Equipamento:</td>
              <td className="valor-destaque" colSpan="3">
                {descricaoEquipamento}
              </td>
              <td className="rotulo">Fornecedor:</td>
              <td className="valor-central">{fornecedor}</td>
            </tr>
            <tr>
              <td className="rotulo">Capacidade:</td>
              <td className="valor-central">{formatarCapacidade(dados.capacidade)}</td>
              <td className="rotulo">Escala calibração:</td>
              <td className="valor-central">{dados.escala || "-"}</td>
              <td className="rotulo">Valor divisão:</td>
              <td className="valor-central">{formatarValorDivisao(dados.valorDivisao)}</td>
            </tr>
            <tr>
              <td className="rotulo">Modelo/Número:</td>
              <td className="valor-central">{dados.modelo || "-"}</td>
              <td className="rotulo">Série:</td>
              <td className="valor-central">{dados.serie || "-"}</td>
              <td className="rotulo">Patrimônio nº:</td>
              <td className="valor-central">{dados.patrimonio || "-"}</td>
            </tr>
          </tbody>
        </table>

        <div className="secao-calibracao">2. PADRÃO UTILIZADO.</div>

        <table className="tabela-calibracao bloco-padrao">
          <colgroup>
            <col style={{ width: "15%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "32%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "9%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Equipamento:</th>
              <th>Identificação</th>
              <th>Laboratório</th>
              <th>Certificado</th>
              <th>Data de Calibração</th>
              <th>Validade</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>CÉLULA DE CARGA</td>
              <td>{identificacaoPadrao}</td>
              <td>{laboratorioPadrao}</td>
              <td>{certificadoPadrao}</td>
              <td>{dataPadrao}</td>
              <td>{validadePadrao}</td>
            </tr>
          </tbody>
        </table>

        <div className="secao-calibracao">3. PROCEDIMENTO.</div>

        <div className="texto-calibracao">
          Calibração realizada conforme norma NBR ISO 7500-1:2023 e ABNT NBR ISO 376
          (versão corrigida de 2021).
          <br />A calibração foi realizada à compressão.
        </div>

        <div className="secao-calibracao">4. RESULTADOS.</div>

        <table className="tabela-calibracao bloco-resultados">
          <colgroup>
            <col style={{ width: "8.5%" }} />
            <col style={{ width: "6.5%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8.5%" }} />
            <col style={{ width: "8.5%" }} />
            <col style={{ width: "10.5%" }} />
            <col style={{ width: "7%" }} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan="11" className="titulo-resultado">
                COMPRESSÃO - Valores em kN
              </th>
            </tr>
            <tr>
              <th colSpan="2">Força lida no item</th>
              <th colSpan="3">Séries de leituras no padrão</th>
              <th colSpan="2">Média</th>
              <th rowSpan="2">Erro Relativo de Indicação %</th>
              <th rowSpan="2">Resolução Relativa</th>
              <th rowSpan="2">Erro Relativo de repetitividade %</th>
              <th rowSpan="2">Classe</th>
            </tr>
            <tr>
              <th>kgf</th>
              <th>kN</th>
              <th>1ª</th>
              <th>2ª</th>
              <th>3ª</th>
              <th>kgf</th>
              <th>kN</th>
            </tr>
          </thead>

          <tbody>
            {resultados.map((resultado, index) => (
              <tr key={`${resultado.kgf}-${index}`} className="linha-carga-resultado">
                <td>{inteiroBR(resultado.kgf)}</td>
                <td>{numeroBR(resultado.kn, 1)}</td>
                <td>{inteiroBR(resultado.l1)}</td>
                <td>{inteiroBR(resultado.l2)}</td>
                <td>{inteiroBR(resultado.l3)}</td>
                <td>{inteiroBR(obterMediaKgf(resultado))}</td>
                <td>{numeroBR(obterMediaKn(resultado), 2)}</td>
                <td>{numeroBR(resultado.erro, 2)}</td>
                <td>{numeroBR(resultado.resolucao, 2)}</td>
                <td>{numeroBR(resultado.repet, 2)}</td>
                <td>{resultado.classe || "-"}</td>
              </tr>
            ))}
            <tr>
              <td rowSpan="3" className="celula-observacao-indice">
                erro relativo do valor de 0%
              </td>
              <td className="texto-esquerda">Antes</td>
              <td>0</td>
              <td>0</td>
              <td>0</td>
              <td colSpan="6" rowSpan="4" className="celula-observacao-final">
                Observação: 1 kgf ≈ 9,80665 Newton
              </td>
            </tr>
            <tr>
              <td className="texto-esquerda">Depois</td>
              <td>0</td>
              <td>0</td>
              <td>0</td>
            </tr>
            <tr>
              <td className="texto-esquerda">f0 =</td>
              <td>0,000 %</td>
              <td>0,000 %</td>
              <td>0,000 %</td>
            </tr>
            <tr>
              <td colSpan="2" className="texto-esquerda">
                f0 max. =
              </td>
              <td colSpan="3">0,000</td>
            </tr>
          </tbody>
        </table>

        <div className="secao-calibracao">5. OBSERVAÇÕES.</div>

        <div className="texto-calibracao observacoes-finais">
          Para laboratórios de ensaio, a máquina de ensaio deve ser classe 1 ou melhor.
          Para laboratórios instalados em obras ou centrais de concreto, admite-se a
          utilização de máquina de ensaio classe 2.
          <br />
          <br />
          Os resultados apresentados neste certificado têm significação restrita e
          referem-se exclusivamente ao material.
        </div>

        <div className="assinatura-calibracao">
          <div className="espaco-assinatura" />

          <div className="responsavel-calibracao">
            <img
              src="/Images/assinaturaLeal.png"
              alt="Assinatura"
              className="imagem-assinatura"
            />
            <div className="linha-assinatura" />
            <strong>José Carlos Lemes Leal</strong>
            <span>Eng. Civil Esp. CREA 203379/D-TO</span>
          </div>
        </div>

        <div className="rodape-restrito">
          DOCUMENTO PARA USO RESTRITO. REPRODUÇÃO/CÓPIA PROIBIDA SEM AUTORIZAÇÃO DA
          EMPRESA.
        </div>
      </div>
    </div>
  );
}

